import React, { useMemo, useCallback, useEffect } from 'react';
import { ArrowLeft, Tag, Sparkles } from 'lucide-react';
import { Instagram, Facebook } from './BrandIcon';
import { getInstagramEmbedUrl, getInstagramLinkUrl } from '../utils';
import { URLS, ENTITY } from '../utils/constants';
import { useIsMobile } from '../hooks/useIsMobile';
import {
  trackPostView,
  trackRelatedPostClick,
  trackSocialClick,
  trackTagClickFromDetail,
  trackVideoEmbed,
} from '../utils/analytics';
import SEO from './SEO';
import { generatePostSchema, extractDescription } from '../utils/seoHelpers';
import Footer from './Footer';

const PostDetail = ({ post, onBack, relatedPosts, onSelectPost, onTagClick }) => {
  const isMobile = useIsMobile();

  useEffect(() => {
    trackPostView(post.id, post.name);
  }, [post.id, post.name]);

  const embedUrl = useMemo(() =>
    getInstagramEmbedUrl(post.instagramUrl),
    [post.instagramUrl]
  );

  useEffect(() => {
    if (embedUrl) {
      trackVideoEmbed(post.id, post.name);
    }
  }, [embedUrl, post.id, post.name]);

  const instagramLinkUrl = useMemo(() =>
    getInstagramLinkUrl(post.instagramUrl, post.shortcode, isMobile),
    [post.instagramUrl, post.shortcode, isMobile]
  );

  const handleTagClick = useCallback((tag) => {
    trackTagClickFromDetail(tag, post.id);
    onTagClick(tag);
  }, [onTagClick, post.id]);

  const handleSelectPost = useCallback((relatedPost) => {
    trackRelatedPostClick(post.id, relatedPost.id, relatedPost.name);
    onSelectPost(relatedPost);
  }, [onSelectPost, post.id]);

  const descriptionLines = useMemo(() =>
    post.description.split('\n'),
    [post.description]
  );

  const slug = post.slug;
  const canonicalUrl = useMemo(() =>
    `${URLS.home}/${ENTITY.routes.detailPath}/${slug}/`,
    [slug]
  );

  const postSchema = useMemo(() =>
    generatePostSchema(post),
    [post]
  );

  const shortDescription = useMemo(() =>
    extractDescription(post.description).substring(0, 160).trim(),
    [post.description]
  );

  // Get items/ingredients field dynamically
  const itemsField = ENTITY.dataFields.items;
  const items = post[itemsField] || [];
  const itemsLabel = ENTITY.labels.sectionItems;

  return (
    <div className="detail-view">
      <SEO
        title={post.name}
        description={shortDescription}
        imageUrl={post.imageUrl}
        imageAlt={post.name}
        canonicalUrl={canonicalUrl}
        keywords={(post.tags || []).join(', ')}
        schema={postSchema}
        ogType="article"
      />
      <main id="main-content">
        <div className="detail-header">
          <button
            className="back-btn"
            onClick={onBack}
            aria-label={ENTITY.labels.backToListAria}
          >
            <ArrowLeft size={24} suppressHydrationWarning />
          </button>
          <h1>{post.name}</h1>
        </div>

        <div className="detail-content">
          {embedUrl && (
            <div className="video-container">
              <iframe
                src={embedUrl}
                frameBorder="0"
                scrolling="no"
                allowFullScreen={true}
                title={post.name}
              />
            </div>
          )}

          <div className="detail-section">
            <div className="tags-container">
              {(post.tags || []).map((tag) => (
                <span
                  key={tag}
                  className="detail-tag"
                  onClick={() => handleTagClick(tag)}
                  style={{ cursor: 'pointer' }}
                >
                  <Tag size={14} suppressHydrationWarning />
                  {` ${tag}`}
                </span>
              ))}
            </div>
          </div>

          <div className="detail-section">
            <h2>
              <Sparkles size={20} suppressHydrationWarning />{' '}
              {ENTITY.labels.sectionDescription}
            </h2>
            <p className="description">
              {descriptionLines.map((line, idx) => (
                <React.Fragment key={idx}>
                  {line}
                  {idx < descriptionLines.length - 1 && <br />}
                </React.Fragment>
              ))}
            </p>
          </div>

          {items.length > 0 && (
            <div className="detail-section">
              <h2>
                <Sparkles size={20} suppressHydrationWarning /> {itemsLabel}
              </h2>
              <ul className="items-list">
                {items.map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="social-links">
            {post.instagramUrl && (
              <a
                href={instagramLinkUrl}
                target="_blank"
                rel={isMobile ? 'noreferrer' : 'noopener noreferrer'}
                className="social-btn instagram"
                aria-label={`${ENTITY.labels.viewOnInstagram()}: ${post.name}`}
                onClick={() => trackSocialClick('instagram', post.id, post.name)}
                suppressHydrationWarning
              >
                <Instagram size={20} suppressHydrationWarning />{' '}
                {ENTITY.labels.viewOnInstagram()}
              </a>
            )}
            {post.facebookUrl && (
              <a
                href={post.facebookUrl}
                target="_blank"
                rel={isMobile ? 'noreferrer' : 'noopener noreferrer'}
                className="social-btn facebook"
                aria-label={`${ENTITY.labels.shareOnFacebook(ENTITY.label)}: ${post.name}`}
                onClick={() => trackSocialClick('facebook', post.id, post.name)}
                suppressHydrationWarning
              >
                <Facebook size={20} suppressHydrationWarning />{' '}
                {ENTITY.labels.shareOnFacebook(ENTITY.label)}
              </a>
            )}
          </div>

          {relatedPosts.length > 0 && (
            <div className="detail-section related-section">
              <h2>{ENTITY.labels.relatedPosts}</h2>
              <div className="related-grid">
                {relatedPosts.map((relatedPost) => {
                  const baseUrl = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
                  const relatedImageUrl = `${baseUrl}/${relatedPost.imageUrl}`;
                  const relatedWebpUrl = relatedImageUrl.replace(/\.(jpg|jpeg|png)$/i, '.webp');
                  return (
                    <div
                      key={relatedPost.id}
                      className="related-card"
                      onClick={() => handleSelectPost(relatedPost)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleSelectPost(relatedPost);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <picture>
                        <source srcSet={relatedWebpUrl} type="image/webp" />
                        <img
                          src={relatedImageUrl}
                          alt={relatedPost.name}
                          loading="lazy"
                        />
                      </picture>
                      <h3>{relatedPost.name}</h3>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PostDetail;
